<template>
    <hox-table ref="table" class="master-templates-table" :has-border="false" :is-loading="isLoading" is-scrollable>
        <template #header>
            <hox-table-header :is-sticky="!isIe">
                <hox-table-header-column
                    v-if="isSelectable"
                    :rowspan="primaryHeaderRowspan"
                    :is-sticky-left="!isIe"
                    :width-pixels="checkboxColumnWidth"
                />

                <hox-table-header-column
                    v-for="column in headers.primary"
                    :key="column.key"
                    :colspan="column.colspan"
                    :has-strong-border-left="column.hasStrongBorderLeft"
                    :is-group-header="column.isGroupHeader"
                    :min-width-pixels="column.minWidthPixels"
                    :max-width-pixels="column.maxWidthPixels"
                    :rowspan="column.rowspan"
                >
                    <span :title="column.title">{{ column.title }}</span>
                </hox-table-header-column>

                <hox-table-header-column
                    :rowspan="primaryHeaderRowspan"
                    :is-sticky-right="!isIe"
                    :width-pixels="actionItemsColumnWidth"
                />
            </hox-table-header>
        </template>

        <template #body>
            <hox-table-row v-for="row in rows" :key="row.masterTemplateId">
                <hox-table-row-column v-if="isSelectable" :is-sticky-left="!isIe" :sticky-offset-pixels="0">
                    <TableCheckboxCell
                        :is-selected="modelValue.includes(row.masterTemplateId)"
                        @selectionChanged="toggleSelection(row.masterTemplateId)"
                    />
                </hox-table-row-column>

                <hox-table-row-column
                    v-for="rowColumn in row.columns"
                    :key="`${row.masterTemplateId}:${rowColumn.key}`"
                    :has-strong-border-left="rowColumn.hasStrongBorderLeft"
                    :max-width-pixels="rowColumn.maxWidthPixels"
                >
                    {{ rowColumn.value }}
                </hox-table-row-column>

                <MasterTemplateBannerFooterOptions
                    :master-template="masterTemplates[row.masterTemplateId]"
                    :options-visible="optionsVisible[row.masterTemplateId] || false"
                    @updateOptionsVisible="onOptionsVisibilityChange(row.masterTemplateId, $event)"
                    @update="$emit('update', row.masterTemplateId)"
                    @familyUpdate="onFamilyUpdate"
                    @remove="$emit('remove', row.masterTemplateId)"
                />
            </hox-table-row>
        </template>

        <template #emptyMessage>
            <slot name="emptyMessage" />
        </template>
    </hox-table>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MasterTemplate } from "hoxton-types";

import { camelToUpperCase, isIe } from "@/utils";
import TableCheckboxCell from "@/components/Campaign/LibraryDeliverableTable/LibraryDeliverableTableCheckboxCell.vue";
import MasterTemplateBannerFooterOptions from "@/components/Previews/BannerFooter/MasterTemplateBannerFooterOptions.vue";

export default defineComponent({
    name: "MasterTemplateTable",
    components: {
        MasterTemplateBannerFooterOptions,
        TableCheckboxCell
    },

    props: {
        allSelectedMode: {
            type: Boolean,
            default: false
        },
        isLoadingDeliverables: {
            type: Boolean
        },

        items: {
            type: Array as PropType<string[]>,
            required: true
        },

        modelValue: {
            type: Array as PropType<string[]>,
            required: true
        },

        isSelectable: {
            type: Boolean
        }
    },

    emits: ["update", "update:modelValue", "creativesSelected", "remove"],

    data() {
        return {
            actionItemsColumnWidth: 40,
            checkboxColumnWidth: 40,
            isIe: isIe(),
            isLoading: false,
            optionsVisible: {} as Record<string, boolean>
        };
    },

    computed: {
        masterTemplates() {
            return this.$store.state.masterTemplateLibrary.masterTemplates;
        },

        headerFields() {
            return ["name", "parentId", "width", "height", "modified"] as (keyof MasterTemplate)[];
        },

        headers() {
            return this.headerFields.reduce(
                (acc, key) => {
                    acc.primary.push({
                        key,
                        maxWidthPixels: this.getColumnWidth(),
                        rowspan: 2,
                        title: this.getColumnTitle(key)
                    });

                    return acc;
                },
                {
                    primary: [] as {
                        key: string;
                        maxWidthPixels: number;
                        rowspan: number;
                        title: string;
                        colspan?: number;
                        hasStrongBorderLeft?: boolean;
                        minWidthPixels?: number;
                        isGroupHeader?: boolean;
                    }[],
                    secondary: []
                }
            );
        },

        primaryHeaderRowspan() {
            return this.headers.secondary.length > 0 ? 2 : 1;
        },

        rows() {
            return this.items.map(masterTemplateId => {
                return this.headerFields.reduce(
                    (acc, key) => {
                        acc.columns.push({
                            ...this.getCellData(masterTemplateId, key),
                            key,
                            maxWidthPixels: this.getColumnWidth()
                        });

                        return acc;
                    },
                    {
                        columns: [] as {
                            value?: string | number;
                            key: string;
                            maxWidthPixels: number;
                            hasStrongBorderLeft?: boolean;
                        }[],
                        masterTemplateId
                    }
                );
            });
        }
    },

    methods: {
        getCellData(masterTemplateId: string, key: keyof MasterTemplate) {
            if (!masterTemplateId || !key) {
                return {};
            }

            const value = this.masterTemplates[masterTemplateId][key];

            if (key === "modified") {
                return {
                    value: new Date((value as number) * 1000).toISOString().split("T")[0]
                };
            }

            if (key === "parentId") {
                return {
                    value: value ? "Yes" : "No"
                };
            }

            return {
                value
            };
        },

        getColumnTitle(key: string, prefix = "") {
            let title = key;

            if (prefix) {
                title = key.replace(`${prefix}:`, "");
            }

            if (key === "parentId") {
                return "Is resize";
            }

            return camelToUpperCase(title);
        },

        getColumnWidth() {
            return 120;
        },

        onFamilyUpdate(masterTemplateIds: string[]) {
            masterTemplateIds.forEach(masterTemplateId => {
                this.$emit("update", masterTemplateId);
            });
        },

        onOptionsVisibilityChange(masterTemplateId: string, value: boolean) {
            this.optionsVisible[masterTemplateId] = value;
        },

        toggleSelection(value: string) {
            const filtered = this.modelValue.filter(label => label !== value);

            if (filtered.length === this.modelValue.length) {
                this.$emit("update:modelValue", [...this.modelValue, value]);
            } else {
                this.$emit("update:modelValue", filtered);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.master-templates-table {
    .table-header-column--group-header {
        font-weight: bold;
    }
}
</style>
